import { clampRatio } from './chart-utils';

interface DonutProps {
  /** Ratio in [0, 1] — clamped defensively (e.g. against a divide-by-zero
   * upstream on an empty dataset). */
  readonly value: number;
  /** Text drawn in the center of the ring (e.g. "72%"), already localized. */
  readonly centerLabel: string;
  /** Summarizes the whole gauge for assistive tech (`role="img"`). */
  readonly ariaLabel: string;
  readonly size?: number;
}

/** A single-value ring gauge (e.g. marketplace fill rate). Direction-agnostic
 * by nature (a progress ring has no reading order), themed via CSS tokens. */
export function Donut({ value, centerLabel, ariaLabel, size = 140 }: DonutProps): JSX.Element {
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = clampRatio(value);
  const dash = circumference * ratio;

  return (
    <div className="chart chart--donut">
      <svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${size} ${size}`} className="chart__svg">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          fill="none"
          className="chart__donutTrack"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="chart__donutValue"
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="chart__donutLabel"
        >
          {centerLabel}
        </text>
      </svg>
    </div>
  );
}
